import "./EditCasePanel.css";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AnimatePresence } from "framer-motion";
import { capitalize, isNumber } from "lodash";
import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import {
  Case,
  CaseAdmin,
  CaseDataEntry,
  CaseDocumentAdmin,
  Clinic,
  PatientFullInsurance,
  PatientGuarantor,
  PatientInsurance,
  SlideAdmin,
} from "pwapi";

import { UserLocation } from "custom-models";

import {
  printLabOrdersSlides,
  reassignAccessionNumberAdminCase,
} from "shared/actions/admin/adminCaseActions";
import { printSlideAdminLabOrder } from "shared/actions/labOrdersActions";
import { useIsLoadingAny } from "shared/hooks/requestStatusHooks";
import { CaseAdminDetail } from "shared/reducers/admin/adminCaseReducer";
import { FeatureFlags } from "shared/reducers/sysReducer";
import { batchPrintSlidesLink, printSlidesLink } from "shared/utils/printUtils";

import Banner from "components/Banner";
import { PathologyWatchButton } from "components/customInputs/buttons/Button";
import { HistoryStateType } from "components/pages/admin/AdminCasePage";
import PatientSexAge from "components/PatientSexAge";
import { useAllowsExternalAccession } from "hooks/useAllowExternalAccession";
import useViewerDocuments from "hooks/useViewerDocuments";
import { useAppDispatch, useAppSelector } from "store/hooks";

import AdminBreadcrumbs from "./AdminBreadcrumbs";
import CaseEditForm from "./CaseEditForm";
import { initialValuesForCase, shallowCaseComparison } from "./caseFormUtils";
import CaseWarningDialog from "./CaseWarningDialog";
import DataEntry from "./DataEntry";
import Documents from "./Documents";
import DocumentsPreviewPanel from "./DocumentsPreviewPanel";
import EmrHeaderDetails from "./EmrHeaderDetails";
import {
  getInitialIntegrationValues,
  IntegrationValuesFormFields,
  shallowIntegrationsComparison,
} from "./integrationFormUtils";
import IntegrationsForm from "./IntegrationsForm";
import Orders from "./Orders";
import PatientForm from "./PatientForm";
import { shallowPatientComparison } from "./patientFormUtils";
import Specimens from "./Specimens";
import AdminPanel from "../adminPanel/AdminPanel";
import { getMostRecentQAReview } from "../adminUtils";
import DocumentsPanel from "../documentsPanel/DocumentsPanel";
import EditSlidePanel from "../editSlidePanel/EditSlidePanel";
import QAReviewInformation from "../QAReviewInformation";
import BlockPanel from "../resourceEditors/blockEditor/BlockPanel";
import GuarantorPanel from "../resourceEditors/guarantorEditor/GuarantorPanel";
import InsurancePanel from "../resourceEditors/insuranceEditor/InsurancePanel";
import LocationPanel from "../resourceEditors/locationEditor/LocationPanel";
import QAReviewPanel from "../resourceEditors/qaReviewEditor/qaReviewPanel";
import SpecimensPanel from "../resourceEditors/specimensEditor/SpecimensPanel";
import SlidesTable from "../SlidesTable/SlidesTable";

type EditCasePanelProps = {
  adminCase: CaseAdminDetail;
  slideId?: number | string;
  specimenId?: number | string;
  blockId?: number | string;
  documentId?: number;
  documentUploading?: boolean;
  isDeleting: boolean;
  setIsDeleting: Function;
  onSavePatient: Function;
  onSaveCase: Function;
  onSaveQAReview: Function;
  onSaveIntegration: Function;
  resetErrorMessage: Function;
  resetDeletionStatus: Function;
  features?: FeatureFlags;
  historyState?: HistoryStateType;
};

const EditCasePanel = (props: EditCasePanelProps) => {
  const {
    adminCase,
    blockId,
    slideId,
    specimenId,
    documentId,
    documentUploading,
    isDeleting,
    setIsDeleting,
    onSavePatient,
    onSaveQAReview,
    onSaveCase,
    onSaveIntegration,
    resetErrorMessage,
    resetDeletionStatus,
    features,
    historyState,
  } = props;

  const dispatch = useAppDispatch();
  const labStains = useAppSelector((state) => state.labStains);

  const isCreatingSlide = !!slideId && !isNumber(slideId);
  const isCreatingBlock = !!blockId && !isNumber(blockId);

  const { requisitionForm, finalReport, prelimReport } =
    useViewerDocuments(adminCase);

  const canUpdateCase =
    adminCase.status === CaseAdmin.status.LIS_PENDING
      ? false
      : (adminCase.status === CaseAdmin.status.FINALIZED ||
          adminCase.status === CaseAdmin.status.ARCHIVED) &&
        // Allow editing of finalized non-diagnostic cases without flag
        adminCase.patient?.clinic?.customer_type !==
          Clinic.customer_type.NON_DIAGNOSTIC
      ? !!features?.editFinalizedCase
      : true;
  const canAddEditSlidesWhenFinalized =
    (adminCase.status === CaseAdmin.status.FINALIZED ||
      adminCase.status === CaseAdmin.status.ARCHIVED) &&
    !!features?.addSlideToFinalizedCase;

  const [previewDocumentId, setPreviewDocumentId] = useState<
    number | undefined
  >(undefined);

  const [editLocation, setEditLocation] = useState<{
    location: UserLocation;
    resource: string;
  } | null>(null);

  const [editInsurance, setEditInsurance] = useState<PatientInsurance | null>(
    null
  );
  const [editGuarantor, setEditGuarantor] = useState<
    PatientGuarantor | undefined
  >(undefined);

  const [isOpenQAReviewDialog, setIsOpenQAReviewDialog] =
    useState<boolean>(false);

  const uncategorizedSlides = useMemo(() => {
    return adminCase
      ? adminCase.slides.filter((slide) => !slide?.specimen_id)
      : [];
  }, [adminCase]);

  const mismatchedSlides = useMemo(() => {
    const specimenIds = (adminCase?.specimens || []).map((spec) => spec.id);
    return adminCase
      ? adminCase.slides.filter(
          (slide) =>
            slide.specimen_id && !specimenIds.includes(slide.specimen_id)
        )
      : [];
  }, [adminCase]);

  // Used for "Print All Slides" Button
  const availableSlides = useMemo(
    () =>
      adminCase.slides.filter(
        (s) =>
          ![
            ...uncategorizedSlides.map((s) => s.id),
            ...mismatchedSlides.map((s) => s.id),
          ].includes(s.id)
      ),
    [adminCase.slides, uncategorizedSlides, mismatchedSlides]
  );

  const [isPatientEdited, setIsPatientEdited] = useState<boolean>(false);
  const patientValues = useRef(adminCase.patient || {});
  const [isCaseEdited, setIsCaseEdited] = useState<boolean>(false);
  const caseValues = useRef<CaseAdminDetail>(adminCase);
  const [isIntegrationEdited, setIsIntegrationEdited] =
    useState<boolean>(false);
  const integrationValues = useRef<any>({
    clinic: adminCase.patient?.clinic,
    external_ezderm_order_id: adminCase.external_ezderm_order_id,
    external_order_id: adminCase.external_order_id,
  });

  const setPatientValues = (newValues: PatientFullInsurance) => {
    patientValues.current = newValues;
  };
  const setCaseValues = (newValues: CaseAdminDetail) => {
    caseValues.current = newValues;
  };
  const setIntegrationValues = (newValues: IntegrationValuesFormFields) => {
    integrationValues.current = newValues;
  };

  const submitPatientForm = (values: PatientFullInsurance) => {
    if (!canUpdateCase) {
      return;
    }

    const submissionForm = shallowPatientComparison(adminCase, values);
    onSavePatient({ id: adminCase.patient.id, ...submissionForm });
    setIsPatientEdited(false);
  };

  const submitCaseForm = (
    values: Partial<ReturnType<typeof initialValuesForCase>>
  ) => {
    if (!canUpdateCase) {
      return;
    }

    const submissionForm = shallowCaseComparison(adminCase, values);
    onSaveCase({ id: adminCase.id, ...submissionForm });
    setIsCaseEdited(false);
  };

  const submitIntegrationForm = (values: IntegrationValuesFormFields) => {
    if (!canUpdateCase) {
      return;
    }
    onSaveIntegration({
      ...shallowIntegrationsComparison(adminCase, values),
      case_id: adminCase?.id,
    });
    setIsIntegrationEdited(false);
  };

  const submitAllForms = () => {
    if (!canUpdateCase) {
      return;
    }
    if (isPatientEdited) {
      submitPatientForm(patientValues.current);
    }
    if (isCaseEdited) {
      submitCaseForm(caseValues.current);
    }
    if (isIntegrationEdited && integrationValues?.current) {
      submitIntegrationForm(integrationValues.current);
    }
  };

  const mostRecentQAReview = getMostRecentQAReview(adminCase.qa_reviews);
  const noteReviews = adminCase.qa_reviews?.filter((r) => r.reviewed === null);
  const reqFormPending =
    !requisitionForm &&
    adminCase.documents?.some(
      (doc) =>
        doc.category === CaseDocumentAdmin.category.REQUISITION_FORM &&
        doc.status === CaseDocumentAdmin.status.PENDING
    );
  const reqFormMissing = !requisitionForm && !reqFormPending;

  const handleReassignAccession = () => {
    adminCase.id &&
      adminCase.accession &&
      adminCase.status !== CaseAdmin.status.ORDERED &&
      dispatch(reassignAccessionNumberAdminCase({ id: adminCase.id }));
  };

  const allowExternalAccession = useAllowsExternalAccession({
    clinic: adminCase.patient?.clinic,
    grossed_by_external_lab_id: adminCase.grossed_by_external_lab_id,
    accessioning_lab: adminCase.accessioning_lab,
    case_type: adminCase.type,
    remains_digital_pc: adminCase.remains_digital_pc,
  });

  const getObjectCountHeader = (type: string, count: number) =>
    `${count} ${type}${count === 1 ? "" : "s"}`;

  const loadingPrintSlideAdminLabOrder = useIsLoadingAny(
    printSlideAdminLabOrder
  );
  const loadingPrintLabOrdersSlides = useIsLoadingAny(printLabOrdersSlides);

  const ordersWithoutSlides = adminCase.lab_orders?.filter(
    (l) =>
      !l.completed_at &&
      !l.cancelled_at &&
      !adminCase.slides.find(
        (s) =>
          s.lab_order_id === l.id ||
          (s.block_obj_id === l.block_id &&
            s.stain_id === labStains[l.lab_stain_id || 0]?.stain_id &&
            s.created_at &&
            l.created_at &&
            s.created_at > l.created_at)
      )
  );

  const data_entry =
    adminCase.data_entries?.find(
      (b) => b.type === CaseDataEntry.type.DATA_ENTRY
    ) || adminCase.data_entry;

  const billing_entry = adminCase.data_entries?.find(
    (b) => b.type === CaseDataEntry.type.BILLING
  );

  return (
    <div className="Admin">
      <AnimatePresence>
        {slideId && (
          <EditSlidePanel
            slideId={
              slideId && !isCreatingSlide ? (slideId as number) : undefined
            }
            isNew={isCreatingSlide}
            adminCase={adminCase}
            specimenId={isNumber(specimenId) ? specimenId : undefined}
            blockId={isNumber(blockId) ? blockId : undefined}
            returnUrl={`/admin/cases/${adminCase.id}`}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {(documentId || documentUploading) && (
          <DocumentsPanel
            documentId={documentId}
            isUploading={documentUploading}
            adminCase={adminCase}
            returnUrl={`/admin/cases/${adminCase.id}`}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {specimenId && !slideId && !blockId && (
          <SpecimensPanel
            returnUrl={`/admin/cases/${adminCase.id}`}
            adminCase={adminCase}
            specimenId={specimenId}
            canUpdateSpecimen={canUpdateCase}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {isNumber(specimenId) && blockId && !slideId && (
          <BlockPanel
            blockId={
              blockId && !isCreatingBlock ? (blockId as number) : undefined
            }
            isNew={isCreatingBlock}
            returnUrl={`/admin/cases/${adminCase.id}`}
            adminCase={adminCase}
            specimenId={specimenId}
            resetErrorMessage={resetErrorMessage}
            resetDeletionStatus={resetDeletionStatus}
            canUpdateSpecimen={canUpdateCase}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {editLocation && (
          <LocationPanel
            accessionCompleted={adminCase.accession_completed}
            adminCase={adminCase}
            billingType={adminCase.billing_type}
            setEditLocation={setEditLocation}
            location={editLocation.location}
            title={`${capitalize(editLocation.resource)} Address`}
            canUpdateLocation={canUpdateCase}
            thirdPartyRequireLocationFields={
              adminCase.accessioning_lab?.settings
                ?.third_party_require_location_fields?.active
            }
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {editInsurance && adminCase.patient.id && (
          <InsurancePanel
            setEditInsurance={setEditInsurance}
            insurance={editInsurance}
            patientId={adminCase.patient.id}
            caseId={adminCase.id || 0}
            allowDelete={adminCase.source === CaseAdmin.source.PW}
            primaryInsurance={
              adminCase.patient.insurance?.find(
                (insur) => insur.primary_provider
              ) as PatientInsurance
            }
            canUpdateInsurance={canUpdateCase}
            insuranceLab={adminCase.accessioning_lab}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {editGuarantor && adminCase.patient?.id && (
          <GuarantorPanel
            accessionCompleted={adminCase.accession_completed}
            setEditGuarantor={setEditGuarantor}
            guarantor={editGuarantor}
            patientId={adminCase.patient.id}
            canUpdateGuarantor={canUpdateCase}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {isOpenQAReviewDialog && (
          <QAReviewPanel
            onClose={() => setIsOpenQAReviewDialog(false)}
            onSubmit={onSaveQAReview}
            qaReviews={adminCase.qa_reviews}
            canReviewHold={features?.qaReviews}
            caseId={adminCase.id || -1}
          />
        )}
      </AnimatePresence>
      {isDeleting && !adminCase.slides.length ? (
        <CaseWarningDialog
          adminCase={adminCase}
          historyState={historyState}
          setIsDeleting={setIsDeleting}
          errorMessage={adminCase.delErrorMessage}
          resetErrorMessage={resetErrorMessage}
        />
      ) : undefined}
      <div className="CasePanel">
        <div className="CasePanel__header">
          <div className="crumbs">
            <AdminBreadcrumbs>
              <Link
                to={historyState?.prevUrl ? historyState.prevUrl : "/admin"}
              >
                {historyState?.label ? historyState.label : "Cases"}
              </Link>
              <FontAwesomeIcon icon="angle-right" />
              <span>{adminCase.accession}</span>
              <EmrHeaderDetails adminCase={adminCase} />
            </AdminBreadcrumbs>

            <div className="actions__container">
              <PathologyWatchButton
                onClick={() => setPreviewDocumentId(requisitionForm?.id)}
                disabled={reqFormMissing || reqFormPending}
                title="Preview Requisition Form"
              >
                <>
                  {reqFormMissing ? (
                    "No"
                  ) : reqFormPending ? (
                    "Pending"
                  ) : (
                    <FontAwesomeIcon
                      icon="file-circle-check"
                      color="var(--green)"
                    />
                  )}{" "}
                  Requisition Form
                </>
              </PathologyWatchButton>
              {!finalReport && prelimReport && (
                <PathologyWatchButton
                  onClick={() => setPreviewDocumentId(prelimReport?.id)}
                  title="Preview Preliminary Report"
                >
                  Preliminary Report
                </PathologyWatchButton>
              )}
              {finalReport && (
                <PathologyWatchButton
                  onClick={() => setPreviewDocumentId(finalReport?.id)}
                  title="Preview Case Report"
                >
                  Case Report
                </PathologyWatchButton>
              )}
              {data_entry && (
                <DataEntry case_id={adminCase.id!} data_entry={data_entry} />
              )}
              {billing_entry && (
                <DataEntry
                  case_id={adminCase.id!}
                  data_entry={billing_entry}
                  holdable
                />
              )}
              {adminCase.source === CaseAdmin.source.PW && (
                <>
                  <div className="actions__QAReview--information">
                    {mostRecentQAReview?.id && (
                      <QAReviewInformation
                        qaReview={mostRecentQAReview}
                        displayIcon
                      />
                    )}
                  </div>
                  <PathologyWatchButton
                    onClick={() => {
                      setIsOpenQAReviewDialog(true);
                    }}
                    className="pwButton--qaReview"
                  >
                    <>
                      QA Reviews
                      {!!noteReviews?.length && (
                        <>
                          &nbsp;
                          <FontAwesomeIcon
                            icon={"comment"}
                            color={"var(--steel)"}
                            fontSize={12}
                          />{" "}
                          <span style={{ fontSize: 12 }}>
                            ({noteReviews.length})
                          </span>
                        </>
                      )}
                    </>
                  </PathologyWatchButton>
                </>
              )}
              <div />
              <PathologyWatchButton
                title="There are no slides in this case"
                color="red"
                onClick={() => setIsDeleting(true)}
                disabled={!!adminCase.slides.length || !canUpdateCase}
              >
                Delete Case
              </PathologyWatchButton>
              {/* TODO: Save All Changes button has been disabled until we can fix validation
                        As implemented, this bypasses per-form validation */}
              {false && (
                <PathologyWatchButton
                  title="There are unsaved changes to this case"
                  color="green"
                  onClick={submitAllForms}
                  disabled={
                    !canUpdateCase ||
                    (!isPatientEdited && !isCaseEdited && !isIntegrationEdited)
                  }
                >
                  Save All Changes
                </PathologyWatchButton>
              )}
            </div>
          </div>
          {!canUpdateCase && (
            <div className="CasePanel__banner CasePanel__banner--red">
              <span className="warningMessage">
                <FontAwesomeIcon icon="exclamation-circle" />
                Case has been finalized. Certain fields may not be edited.
              </span>
            </div>
          )}
          {adminCase?.source === Case.source.PW &&
            adminCase.accession &&
            adminCase.status !== CaseAdmin.status.ORDERED &&
            adminCase.grossed_by_cert?.prefix?.id &&
            adminCase.accession_prefix?.id !==
              adminCase.grossed_by_cert?.prefix?.id &&
            !allowExternalAccession && (
              <div className="CasePanel__banner CasePanel__banner--red">
                <span className="warningMessage">
                  <FontAwesomeIcon icon="exclamation-circle" />
                  The accession prefix of this case may be incorrect.
                  {features?.editFinalizedCase ? (
                    <button
                      className="pwButton pwButton--red"
                      onClick={() => handleReassignAccession()}
                    >
                      Re-Assign to {adminCase.grossed_by_cert?.prefix?.prefix}
                    </button>
                  ) : (
                    <>
                      &nbsp;Please contact support for a prefix change to
                      <strong>
                        {adminCase.grossed_by_cert?.prefix?.prefix}
                      </strong>
                    </>
                  )}
                </span>
              </div>
            )}
          {adminCase.status === CaseAdmin.status.ERROR && (
            <div className="CasePanel__banner CasePanel__banner--red">
              <span className="warningMessage">
                <FontAwesomeIcon icon="exclamation-circle" />
                <strong>Warning:</strong> This case is in <code>error</code>{" "}
                status.
              </span>
              {/* TODO: Would be good to expose `external_source` via serializer here.
                  ...   This would help us display additional context on why the case is errored
                  ...   We would also want to expose duplicate_case for an even better message */}
            </div>
          )}
          {!adminCase.accession_completed && (
            <Banner type="alert">
              Case has not been accessioned. Please click{" "}
              <Link
                to={`${process.env.REACT_APP_BACKEND_PROTOCOL}://${process.env.REACT_APP_LIS_DOMAIN}/cases/${adminCase.id}`}
                target="_blank"
              >
                here
              </Link>{" "}
              to complete case accession.
            </Banner>
          )}
        </div>

        <div className="patient_form">
          <AdminPanel
            title={{
              prefix: `Patient #${adminCase.patient?.id}`,
              main: (
                <>
                  {adminCase.patient?.last_name},{" "}
                  {adminCase.patient?.first_name}{" "}
                  <span>
                    (
                    <PatientSexAge
                      patient={adminCase.patient}
                      shouldDisplayDOB={false}
                    />
                    )
                  </span>
                </>
              ),
            }}
            isSaving={adminCase.isPatientSaving}
          >
            <PatientForm
              accessionCompleted={adminCase.accession_completed}
              patient={adminCase.patient}
              orderingDoctor={adminCase.ordering_doctor}
              setValues={setPatientValues}
              onSubmit={submitPatientForm}
              setEdited={setIsPatientEdited}
              setEditLocation={setEditLocation}
              setEditInsurance={setEditInsurance}
              setEditGuarantor={setEditGuarantor}
              canUpdateCase={canUpdateCase}
              caseSource={adminCase.source || CaseAdmin.source.WPL}
              caseType={adminCase.type}
            />
          </AdminPanel>
        </div>
        <div className="case_form">
          <AdminPanel
            title={{
              prefix: `Case #${adminCase.id}`,
              main: adminCase.accession,
            }}
            oppositeTitle={
              adminCase?.patient?.clinic?.external_clinic_id?.match(/^[GDP]\d+/)
                ? {
                    prefix: adminCase?.patient?.clinic?.name,
                    main: adminCase?.patient?.clinic?.external_clinic_id,
                  }
                : undefined
            }
            isSaving={adminCase.isCaseSaving}
          >
            <CaseEditForm
              accessionCompleted={adminCase.accession_completed}
              adminCase={adminCase}
              setValues={setCaseValues}
              onSubmit={submitCaseForm}
              isEdited={isCaseEdited}
              canUpdateCase={canUpdateCase}
              setEdited={setIsCaseEdited}
            />
          </AdminPanel>
        </div>
        <div className="integrations">
          <AdminPanel
            title={{
              main: "Integrations",
            }}
            isSaving={adminCase.isIntegrationSaving}
          >
            <IntegrationsForm
              initialFields={getInitialIntegrationValues(adminCase)}
              adminCase={adminCase}
              onSubmit={submitIntegrationForm}
              setValues={setIntegrationValues}
              setEdited={setIsIntegrationEdited}
              canUpdateCase={canUpdateCase}
            />
          </AdminPanel>
        </div>
        <div className="specimens">
          {(!!uncategorizedSlides.length || !!mismatchedSlides.length) && (
            <AdminPanel
              title={{
                prefix: "Slides with either mismatched or missing specimens",
                main: getObjectCountHeader(
                  "Slide",
                  uncategorizedSlides.length + mismatchedSlides.length
                ),
              }}
            >
              <div className="panelContainer panelContainer--error">
                <div>
                  <SlidesTable
                    slides={[...uncategorizedSlides, ...mismatchedSlides]}
                    slideEditUrl={`/admin/cases/${adminCase.id}/slides/`}
                    adminCase={adminCase}
                  />
                </div>
              </div>
            </AdminPanel>
          )}
          <AdminPanel
            title={{
              prefix: getObjectCountHeader(
                "Specimen",
                adminCase?.specimens?.length || 0
              ),
              main: (
                <>
                  {getObjectCountHeader(
                    "Slide",
                    adminCase.slides.length -
                      uncategorizedSlides.length -
                      mismatchedSlides.length
                  )}
                  {!!availableSlides?.length && (
                    <Link
                      to={printSlidesLink(
                        adminCase.id!,
                        availableSlides,
                        adminCase.accessioning_lab?.settings?.admin_zebra_print
                          ?.active
                          ? "zebra"
                          : undefined
                      )}
                      target="_blank"
                      rel="opener"
                      className="PWTable__actions__link PWTable__actions__link--small"
                    >
                      Print All Slides <FontAwesomeIcon icon="print" />
                    </Link>
                  )}
                </>
              ),
            }}
            actions={
              <Link
                to={`/admin/cases/${adminCase.id}/specimens/new`}
                style={{ paddingBottom: "0.3em" }}
              >
                <PathologyWatchButton color="green">
                  + Create New Specimen
                </PathologyWatchButton>
              </Link>
            }
          >
            <Specimens
              adminCase={adminCase}
              canUpdateCase={canUpdateCase}
              canAddEditSlidesWhenFinalized={canAddEditSlidesWhenFinalized}
            />
          </AdminPanel>
        </div>
        {adminCase.lab_orders?.length ? (
          <div className="orders">
            <AdminPanel
              title={{
                main: getObjectCountHeader(
                  "Order",
                  adminCase?.lab_orders?.length || 0
                ),
              }}
              oppositeTitle={{
                main: features?.printSlideFromOrder &&
                  !!ordersWithoutSlides?.length && (
                    <PathologyWatchButton
                      size="small"
                      onClick={() => {
                        adminCase.id &&
                          !loadingPrintSlideAdminLabOrder &&
                          !loadingPrintLabOrdersSlides &&
                          dispatch(
                            printLabOrdersSlides({ id: adminCase.id })
                          ).then((r: SlideAdmin[]) => {
                            window.open(
                              batchPrintSlidesLink(
                                [
                                  ...new Set(
                                    r
                                      .map((s) => s.case?.id)
                                      .filter((v): v is number => Boolean(v))
                                  ),
                                ],
                                r
                                  .map((s) => s.id)
                                  .filter((v): v is number => Boolean(v)),
                                "zebra"
                              )
                            );
                          });
                      }}
                      disabled={
                        loadingPrintSlideAdminLabOrder ||
                        loadingPrintLabOrdersSlides
                      }
                    >
                      Create Slides for Orders Without Printed Slides
                    </PathologyWatchButton>
                  ),
              }}
            >
              <Orders
                adminCase={adminCase}
                ordersWithoutSlides={ordersWithoutSlides}
              />
            </AdminPanel>
          </div>
        ) : undefined}
        <div className="documents">
          <AdminPanel
            title={{
              main: getObjectCountHeader(
                "Document",
                adminCase?.documents?.length || 0
              ),
            }}
            actions={
              <Link
                to={`/admin/cases/${adminCase.id}/documents/upload`}
                style={{
                  paddingBottom: "0.3em",
                  pointerEvents:
                    adminCase.status === CaseAdmin.status.LIS_PENDING
                      ? "none"
                      : "all",
                }}
              >
                <PathologyWatchButton
                  color="green"
                  disabled={adminCase.status === CaseAdmin.status.LIS_PENDING}
                >
                  Upload Document
                </PathologyWatchButton>
              </Link>
            }
          >
            <Documents
              adminCase={adminCase}
              documents={adminCase.documents as CaseDocumentAdmin[]}
              setPreviewDocumentId={setPreviewDocumentId}
            />
          </AdminPanel>
        </div>
      </div>

      {previewDocumentId && (
        <DocumentsPreviewPanel
          documentId={previewDocumentId}
          closePanel={setPreviewDocumentId}
        />
      )}
    </div>
  );
};

export default EditCasePanel;
